// frontend/src/components/Navbar.js
import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { useCurrentBasho } from '../hooks/useCurrentBasho'
import { clearAuth } from '../auth'
import api from '../api'

const TOTAL_DAYS = 15
const font = "'Noto Serif JP', Georgia, serif"

const MONTH_NAMES = { '01': 'January', '03': 'March', '05': 'May', '07': 'July', '09': 'September', '11': 'November' }

function fmtBashoShort(id) {
  if (!id) return ''
  const mm = id.slice(4, 6)
  return `${MONTH_NAMES[mm] || mm} ${id.slice(0, 4)}`
}

// Dropdown uses the same format as the label
function fmtBashoDropdown(id) {
  return fmtBashoShort(id)
}

// Module-level cache so every Navbar mount reuses the same list
let bashoHistoryCache = null
let bashoHistoryCachedAt = null
const HISTORY_CACHE_TTL = 60 * 60 * 1000

export default function Navbar({ currentPage, backLink }) {
  const navigate = useNavigate()
  const params = useParams()
  const { basho: detectedBasho, day: detectedDay } = useCurrentBasho()
  const basho = params.basho || detectedBasho
  const day = parseInt(params.day || detectedDay || 1, 10)
  const currentBashoDay = parseInt(detectedDay || 1, 10)
  const username = localStorage.getItem('username')
  const isAdmin = localStorage.getItem('isAdmin') === 'true'
  const [menuOpen, setMenuOpen] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [bashoHistory, setBashoHistory] = useState([])
  const historyRef = useRef(null)

  // Current basho being browsed is different from the live current basho
  const isPastBasho = !!(basho && detectedBasho && basho !== detectedBasho)

  // Fetch lightweight basho history for dropdown (cached)
  useEffect(() => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token')
    if (!token) return
    if (bashoHistoryCache && bashoHistoryCachedAt && Date.now() - bashoHistoryCachedAt < HISTORY_CACHE_TTL) {
      setBashoHistory(bashoHistoryCache)
      return
    }
    api.get('/basho/history')
      .then(res => {
        const list = res.data.bashos || []
        bashoHistoryCache = list
        bashoHistoryCachedAt = Date.now()
        setBashoHistory(list)
      })
      .catch(() => {})
  }, [])

  // Close history dropdown on outside click
  useEffect(() => {
    if (!historyOpen) return
    const handler = (e) => {
      if (historyRef.current && !historyRef.current.contains(e.target)) {
        setHistoryOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [historyOpen])

  const handleLogout = () => {
    clearAuth()
    navigate('/login')
  }

  const goToDay = (d) => {
    setMenuOpen(false)
    if (currentPage === 'leaderboard') navigate(`/leaderboard/${basho}/${d}`)
    else navigate(`/picks/${basho}/${d}`)
  }

  const getDayStyle = (d) => {
    if (d === day) return { ...s.dayBtn, background: '#C41E3A', color: '#FFFFFF', border: '1px solid #C41E3A', fontWeight: 700 }
    if (d <= currentBashoDay) return { ...s.dayBtn, background: '#5C3A1E', color: '#D4C8A8', border: '1px solid #5C3A1E' }
    return { ...s.dayBtn, background: 'transparent', color: '#C8BFA8', border: '1px solid #E8E0D0', opacity: 0.5 }
  }

  // All completed basho from history — show everything (most recent first from API)
  const pastBashoList = bashoHistory

  const handleHistoryClick = (b) => {
    setHistoryOpen(false)
    navigate(`/picks/${b.basho}/${b.lastDay}`)
  }

  const handleBackToCurrent = () => {
    setHistoryOpen(false)
    navigate(`/picks/${detectedBasho}/${currentBashoDay}`)
  }

  // Picks nav link always goes to current basho
  const picksTo = detectedBasho ? `/picks/${detectedBasho}/${currentBashoDay}` : '/picks'

  return (
    <nav style={s.nav}>
      <style>{`
        @media (max-width: 768px) {
          .nav-center-links { display: none !important; }
          .nav-hamburger { display: block !important; }
          .nav-username-link, .nav-right-sep, .nav-logout-link { display: none !important; }
          .nav-day-btn { width: 22px !important; height: 22px !important; font-size: 10px !important; }
          .nav-basho-label { font-size: 10px !important; }
        }
      `}</style>
      <div style={s.topBar}>
        {/* Left: seal + title */}
        <Link to="/" style={s.logoWrap}>
          <div style={s.seal}><span style={s.sealText}>相撲{'\n'}番付</span></div>
          <span style={s.logoText}>Sumo <span style={s.logoAccent}>Pick'em</span></span>
        </Link>

        {/* Center: nav links */}
        <div className="nav-center-links" style={s.centerLinks}>
          <Link
            to={picksTo}
            style={{ ...s.navLink, ...(currentPage === 'picks' ? s.navLinkActive : {}) }}
          >
            Picks
          </Link>
          <Link
            to={`/leaderboard/${basho}/${day}`}
            style={{ ...s.navLink, ...(currentPage === 'leaderboard' ? s.navLinkActive : {}) }}
          >
            Leaderboard
          </Link>
        </div>

        {/* Right: username (→ profile) · admin · log out */}
        <div style={s.rightArea}>
          <Link to="/profile" className="nav-username-link" style={s.usernameLink}>{username}</Link>
          <span className="nav-right-sep" style={s.rightSep}>·</span>
          {isAdmin && (
            <>
              <Link to="/admin" className="nav-username-link" style={s.adminLink}>Admin</Link>
              <span className="nav-right-sep" style={s.rightSep}>·</span>
            </>
          )}
          <button className="nav-logout-link" onClick={handleLogout} style={s.logoutLink}>Log out</button>
          <button
            className="nav-hamburger"
            onClick={() => setMenuOpen(o => !o)}
            style={s.hamburger}
            aria-label="Menu"
          >
            {menuOpen ? '✕' : '☰'}
          </button>
        </div>
      </div>

      {/* Day selector — picks + rankings (not profile, leaderboard, or when backLink provided) */}
      {currentPage !== 'profile' && currentPage !== 'leaderboard' && !backLink && (
        <div style={s.dayRowOuter}>
          {/* Scrollable inner section: basho label + day buttons + rankings.
              overflow-x:auto on this inner div clips overflow-y too, so the
              Past Basho dropdown lives OUTSIDE this div to avoid being clipped. */}
          <div style={s.dayRowScroll}>
            {basho && (
              <>
                <span className="nav-basho-label" style={s.bashoNameLabel}>
                  {fmtBashoShort(basho)}
                  {isPastBasho && <span style={s.pastTag}> past</span>}
                </span>
                <div className="nav-basho-sep" style={s.bashoSep} />
              </>
            )}
            <span style={s.dayLabel}>DAY</span>
            <div style={s.dayGrid}>
              {Array.from({ length: TOTAL_DAYS }, (_, i) => i + 1).map(d => (
                <button key={d} className="nav-day-btn" onClick={() => goToDay(d)} style={getDayStyle(d)}>
                  {d}
                </button>
              ))}
            </div>
            <div style={s.daySep} />
            <Link
              to={isPastBasho ? `/rankings?basho=${basho}` : '/rankings'}
              style={s.rankingsBtn}
            >
              Rankings
            </Link>
          </div>

          {/* Past Basho dropdown — outside the overflow container so it can extend below the bar */}
          <div ref={historyRef} style={s.pastBashoWrap}>
            <button
              onClick={() => setHistoryOpen(o => !o)}
              style={s.pastBashoBtn(isPastBasho)}
              title="Browse past basho"
            >
              {isPastBasho ? '← Basho' : 'Past ▾'}
            </button>
            {historyOpen && (
              <div style={s.historyDropdown}>
                {isPastBasho && (
                  <button onClick={handleBackToCurrent} style={s.historyItemCurrent}>
                    ← Current basho
                  </button>
                )}
                {pastBashoList.length === 0 ? (
                  <div style={s.historyEmpty}>No past basho yet</div>
                ) : (
                  pastBashoList.map(b => (
                    <button
                      key={b.basho}
                      onClick={() => handleHistoryClick(b)}
                      style={{
                        ...s.historyItem,
                        ...(b.basho === basho ? s.historyItemActive : {}),
                      }}
                    >
                      {fmtBashoDropdown(b.basho)}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Back link bar — historical picks view */}
      {backLink && (
        <div style={s.backLinkRow}>
          <Link to={backLink.to} style={s.backLinkStyle}>{backLink.label}</Link>
        </div>
      )}

      {/* Mobile menu */}
      {menuOpen && (
        <div style={s.mobileMenu}>
          <Link to={picksTo} style={s.mobileLink} onClick={() => setMenuOpen(false)}>Picks</Link>
          <Link to={`/leaderboard/${basho}/${day}`} style={s.mobileLink} onClick={() => setMenuOpen(false)}>Leaderboard</Link>
          <div style={s.mobileDivider} />
          <Link to="/profile" style={{ ...s.mobileLink, color: '#5C3A1E' }} onClick={() => setMenuOpen(false)}>{username}</Link>
          {isAdmin && (
            <Link to="/admin" style={{ ...s.mobileLink, color: '#C41E3A' }} onClick={() => setMenuOpen(false)}>Admin</Link>
          )}
          <button onClick={handleLogout} style={s.mobileLogout}>Log Out</button>
        </div>
      )}
    </nav>
  )
}

const s = {
  nav: {
    background: '#F5F0E8',
    borderBottom: '2px solid #1A0A00',
    position: 'sticky', top: 0, zIndex: 100,
  },
  topBar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '0 20px', height: 52,
  },
  logoWrap: {
    display: 'flex', alignItems: 'center', gap: 10,
    textDecoration: 'none', flexShrink: 0,
  },
  seal: {
    width: 28, height: 28, background: '#C41E3A', borderRadius: 3,
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  sealText: {
    color: '#F5F0E8', fontSize: 7, fontWeight: 700, textAlign: 'center',
    lineHeight: 1.2, whiteSpace: 'pre', fontFamily: "'Noto Serif JP', Georgia, serif",
  },
  logoText: {
    color: '#1A0A00', fontFamily: "'Noto Serif JP', Georgia, serif",
    fontWeight: 700, fontSize: 16, letterSpacing: 0.5,
  },
  logoAccent: { color: '#C41E3A' },
  centerLinks: {
    display: 'flex', gap: 28, position: 'absolute',
    left: '50%', transform: 'translateX(-50%)',
  },
  navLink: {
    color: '#8A7A5A', textDecoration: 'none',
    fontFamily: "'Noto Serif JP', Georgia, serif",
    fontSize: 11, letterSpacing: 2, textTransform: 'uppercase',
  },
  navLinkActive: { color: '#1A0A00', fontWeight: 700 },
  rightArea: { display: 'flex', alignItems: 'center', gap: 8 },
  usernameLink: {
    color: '#5C3A1E', fontSize: 11, fontFamily: 'Georgia, serif',
    textDecoration: 'none', letterSpacing: 0.5, cursor: 'pointer',
  },
  adminLink: {
    color: '#C41E3A', fontSize: 11, fontFamily: 'Georgia, serif',
    textDecoration: 'none', letterSpacing: 0.5, cursor: 'pointer',
  },
  rightSep: {
    color: '#C4B89A', fontSize: 11, fontFamily: 'Georgia, serif',
    userSelect: 'none',
  },
  logoutLink: {
    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
    color: '#5C3A1E', fontSize: 11, fontFamily: 'Georgia, serif',
    letterSpacing: 0.5,
  },
  hamburger: {
    display: 'none', background: 'transparent', border: 'none',
    color: '#8A7A5A', fontSize: 18, cursor: 'pointer', padding: '4px 8px',
  },
  dayRowOuter: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '6px 20px 8px',
    background: '#EDE8DC', borderTop: '1px solid #D4C8A8',
    // No overflow here — lets the dropdown extend below the bar
  },
  dayRowScroll: {
    display: 'flex', alignItems: 'center', gap: 8,
    flex: 1, minWidth: 0, overflowX: 'auto',
  },
  pastBashoWrap: {
    position: 'relative', flexShrink: 0,
  },
  bashoNameLabel: {
    fontSize: 11, fontWeight: 700, color: '#1A0A00',
    fontFamily: "'Noto Serif JP', Georgia, serif",
    whiteSpace: 'nowrap', flexShrink: 0, letterSpacing: 0.3,
  },
  pastTag: {
    fontSize: 9, color: '#8A7A5A', fontWeight: 400,
    letterSpacing: 1, textTransform: 'uppercase',
  },
  bashoSep: {
    width: 1, height: 16, background: '#C4B89A', flexShrink: 0,
  },
  dayLabel: {
    fontSize: 9, color: '#1A0A00', letterSpacing: 2,
    fontFamily: "'Noto Serif JP', Georgia, serif",
    textTransform: 'uppercase', whiteSpace: 'nowrap',
  },
  backLinkRow: {
    padding: '6px 20px 8px', background: '#EDE8DC', borderTop: '1px solid #D4C8A8',
  },
  backLinkStyle: {
    fontSize: 11, color: '#8A7A5A', fontFamily: 'Georgia, serif',
    textDecoration: 'none', letterSpacing: 1,
  },
  dayGrid: { display: 'flex', gap: 3, flexWrap: 'nowrap' },
  daySep: { width: 1, height: 18, background: '#C4B89A', flexShrink: 0 },
  rankingsBtn: {
    display: 'inline-flex', alignItems: 'center',
    padding: '0 10px', height: 26, flexShrink: 0,
    background: '#5C3A1E', color: '#D4C8A8',
    border: '1px solid #5C3A1E', borderRadius: 2,
    fontSize: 10, fontFamily: "'Noto Serif JP', Georgia, serif",
    letterSpacing: 1.5, textTransform: 'uppercase',
    textDecoration: 'none', whiteSpace: 'nowrap',
    transition: 'all 0.1s',
  },
  pastBashoBtn: (isPast) => ({
    display: 'inline-flex', alignItems: 'center',
    padding: '0 8px', height: 26, flexShrink: 0,
    background: isPast ? '#C41E3A' : 'transparent',
    color: isPast ? '#F5F0E8' : '#8A7A5A',
    border: `1px solid ${isPast ? '#C41E3A' : '#C4B89A'}`,
    borderRadius: 2, fontSize: 10,
    fontFamily: "'Noto Serif JP', Georgia, serif",
    letterSpacing: 1, cursor: 'pointer',
    whiteSpace: 'nowrap', transition: 'all 0.1s',
  }),
  historyDropdown: {
    position: 'absolute', top: '100%', right: 0, marginTop: 4,
    background: '#FFFFFF', border: '1px solid #D4C8A8', borderRadius: 4,
    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
    minWidth: 180, zIndex: 200,
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
    maxHeight: 280, overflowY: 'auto',
  },
  historyItemCurrent: {
    display: 'block', width: '100%', padding: '10px 14px',
    background: '#F5F0E8', border: 'none', borderBottom: '1px solid #E8E0D0',
    fontSize: 12, fontFamily: "'Noto Serif JP', Georgia, serif",
    color: '#5C3A1E', fontWeight: 700, textAlign: 'left',
    cursor: 'pointer', letterSpacing: 0.3,
  },
  historyItem: {
    display: 'block', width: '100%', padding: '9px 14px',
    background: 'transparent', border: 'none', borderBottom: '1px solid #F5F0E8',
    fontSize: 12, fontFamily: "'Noto Serif JP', Georgia, serif",
    color: '#1A0A00', textAlign: 'left', cursor: 'pointer',
    letterSpacing: 0.3, transition: 'background 0.1s',
  },
  historyItemActive: {
    background: '#FEF0F0', color: '#C41E3A', fontWeight: 700,
  },
  historyEmpty: {
    padding: '12px 14px', fontSize: 11, color: '#8A7A5A',
    fontFamily: 'Georgia, serif', fontStyle: 'italic',
  },
  dayBtn: {
    width: 26, height: 26, borderRadius: 2, cursor: 'pointer',
    fontSize: 11, fontFamily: "'Noto Serif JP', Georgia, serif",
    flexShrink: 0, transition: 'all 0.1s', padding: 0,
  },
  mobileMenu: {
    display: 'flex', flexDirection: 'column',
    padding: '8px 0 16px', borderTop: '1px solid #D4C8A8',
    background: '#F5F0E8',
  },
  mobileLink: {
    color: '#1A0A00', textDecoration: 'none',
    fontFamily: "'Noto Serif JP', Georgia, serif",
    fontSize: 15, padding: '12px 24px', letterSpacing: 1,
  },
  mobileDivider: { height: 1, background: '#D4C8A8', margin: '8px 0' },
  mobileLogout: {
    background: 'transparent', border: 'none', color: '#5C3A1E',
    fontFamily: 'Georgia, serif',
    fontSize: 15, padding: '12px 24px', textAlign: 'left', cursor: 'pointer',
  },
}
